// =====================================================
// 포인트 적립 관리 탭 — point_earn_rules 규칙별 포인트/활성 설정
// =====================================================
import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'

interface EarnRule {
  id: number
  rule_type: string
  rule_name: string
  service_type: string
  points: number
  is_active: number
}

const SERVICE_TYPE_LABEL: Record<string, string> = {
  MEMBER: '회원',
  PROGRAM: '프로그램',
  CONSULT: '상담',
  SPACE: '공간예약',
  LOCKER: '금고',
  LOAN: '도서',
}

const LIMIT = 10

export default function EarnRuleTab() {
  const [rules, setRules] = useState<EarnRule[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  // 규칙별 입력값 (id → 입력 중인 포인트 문자열)
  const [inputs, setInputs] = useState<Record<number, string>>({})
  const [savingId, setSavingId] = useState<number | null>(null)
  const [msg, setMsg] = useState<{ id: number; text: string; ok: boolean } | null>(null)

  const fetchRules = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/point/admin/earn-rules'))
      if (!res.ok) throw new Error()
      const json = await res.json()
      const list: EarnRule[] = json.earn_rules ?? []
      setRules(list)
      setInputs(Object.fromEntries(list.map(r => [r.id, String(r.points)])))
    } catch {
      alert('적립 규칙을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchRules() }, [])

  const patchRule = async (rule: EarnRule, body: { points?: number; is_active?: number }) => {
    setSavingId(rule.id)
    setMsg(null)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/point/admin/earn-rules/${rule.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.detail?.message ?? json?.detail ?? '저장 실패')
      setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, points: json.points, is_active: json.is_active } : r)))
      setInputs(prev => ({ ...prev, [rule.id]: String(json.points) }))
      setMsg({ id: rule.id, text: '저장되었습니다.', ok: true })
    } catch (e: any) {
      setMsg({ id: rule.id, text: e.message ?? '저장에 실패했습니다.', ok: false })
    } finally {
      setSavingId(null)
    }
  }

  const handleSavePoints = (rule: EarnRule) => {
    const raw = (inputs[rule.id] ?? '').trim()
    const num = Number(raw)
    if (raw === '' || !Number.isInteger(num)) {
      setMsg({ id: rule.id, text: '정수를 입력해주세요.', ok: false })
      return
    }
    patchRule(rule, { points: num })
  }

  const handleToggleActive = (rule: EarnRule) => {
    patchRule(rule, { is_active: rule.is_active === 1 ? 0 : 1 })
  }

  const totalPages = Math.ceil(rules.length / LIMIT)
  const pagedRules = rules.slice((page - 1) * LIMIT, page * LIMIT)

  return (
    <>
    <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
      <div className="px-5 py-4 border-b border-[#f5f2f0] flex items-end justify-between">
        <div>
          <p className="m-0 text-[14px] font-bold text-[#5a3d32]">적립 규칙</p>
          <p className="m-0 mt-0.5 text-[12px] text-gray-400">
            각 활동 시 지급되는 포인트(마루)를 설정합니다. 비활성화된 규칙은 포인트가 지급되지 않습니다.
          </p>
        </div>
        <div className="text-[13px] text-gray-400 font-medium whitespace-nowrap">
          총 <span className="text-[#887267] font-bold">{rules.length}</span>건
        </div>
      </div>

      {loading ? (
        <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">불러오는 중...</div>
      ) : rules.length === 0 ? (
        <div className="min-h-[200px] flex items-center justify-center text-gray-300 text-[13px]">
          등록된 적립 규칙이 없습니다. (point_earn_rules 테이블 확인 필요)
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
              <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">서비스</th>
              <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">규칙명</th>
              <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[220px]">포인트</th>
              <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">활성</th>
            </tr>
          </thead>
          <tbody>
            {pagedRules.map(rule => (
              <tr key={rule.id} className={`border-t border-[#f5f2f0] transition-colors ${rule.is_active === 1 ? 'hover:bg-[#fdf8f0]' : 'bg-[#fafafa] opacity-60'}`}>
                <td className="px-4 py-3">
                  <span className="text-[11px] px-2 py-0.5 rounded font-medium bg-[#f5f2f0] text-[#5a3d32]">
                    {SERVICE_TYPE_LABEL[rule.service_type] ?? rule.service_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[13px] font-medium text-[#2a1f1a]">{rule.rule_name}</span>
                  <span className="text-[11px] text-gray-300 ml-2 font-mono">{rule.rule_type}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={inputs[rule.id] ?? ''}
                      onChange={e => { setInputs(prev => ({ ...prev, [rule.id]: e.target.value })); setMsg(null) }}
                      onKeyDown={e => e.key === 'Enter' && handleSavePoints(rule)}
                      className="w-24 h-8 px-2 border border-[#e8e0dc] rounded-lg text-[13px] text-right outline-none focus:border-[#887267]"
                    />
                    <span className="text-[12px] text-gray-400">마루</span>
                    <button
                      onClick={() => handleSavePoints(rule)}
                      disabled={savingId === rule.id}
                      className="h-8 px-3 bg-[#887267] text-white rounded-lg text-[12px] font-semibold hover:bg-[#6b5248] transition-colors disabled:opacity-50"
                    >
                      {savingId === rule.id ? '저장중' : '저장'}
                    </button>
                  </div>
                  {msg?.id === rule.id && (
                    <p className={`m-0 mt-1 text-[11px] ${msg.ok ? 'text-green-600' : 'text-red-400'}`}>{msg.text}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleToggleActive(rule)}
                    disabled={savingId === rule.id}
                    className={`text-[11px] px-3 py-1 rounded-full font-semibold transition-colors disabled:opacity-50 ${
                      rule.is_active === 1
                        ? 'bg-[#e8f5e9] text-[#388e3c] hover:bg-[#dcedc8]'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    {rule.is_active === 1 ? '활성' : '비활성'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>

    {/* Pagination */}
    {totalPages > 1 && (
      <div className="flex justify-center items-center gap-1.5 mt-5">
        <button
          onClick={() => setPage(p => p - 1)}
          disabled={page === 1}
          className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
        >‹</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
              page === p
                ? 'bg-[#887267] text-white border-[#887267]'
                : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
            }`}
          >{p}</button>
        ))}
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={page === totalPages}
          className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
        >›</button>
      </div>
    )}
    </>
  )
}
